import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { OpportunityInterestListItem } from '../../lib/types'
import { opportunityInterestServices } from '../../services'
import { AllocationRequestsListPresenter } from './allocation-requests-list-presenter'

const PAGE_SIZE = 10

export function AllocationRequestsListContainer() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [interests, setInterests] = useState<OpportunityInterestListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [keyword, setKeyword] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const load = useCallback(async () => {
    setIsLoading(true)
    const data = await opportunityInterestServices.listAll()
    if (data.status === 200) {
      setInterests(data.interests)
    } else {
      setInterests([])
      dispatch(
        showBanner({
          text: 'Failed to load allocation requests',
          variant: 'error-banner',
        }),
      )
    }
    setIsLoading(false)
  }, [dispatch])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase()
    if (!q) return interests
    return interests.filter((row) => {
      const name = `${row.first_name ?? ''} ${row.last_name ?? ''}`.toLowerCase()
      return (
        name.includes(q) ||
        (row.investor_email ?? '').toLowerCase().includes(q) ||
        (row.investor_reference ?? '').toLowerCase().includes(q) ||
        (row.opportunity_title ?? '').toLowerCase().includes(q) ||
        (row.opportunity_slug ?? '').toLowerCase().includes(q) ||
        String(row.id).includes(q) ||
        String(row.investor_id).includes(q) ||
        String(row.opportunity_id).includes(q)
      )
    })
  }, [interests, keyword])

  const pagination = useMemo(() => {
    const total = filtered.length
    const offset = (currentPage - 1) * PAGE_SIZE
    return {
      total,
      limit: PAGE_SIZE,
      count: Math.min(PAGE_SIZE, Math.max(0, total - offset)),
      offset,
    }
  }, [filtered.length, currentPage])

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filtered.slice(start, start + PAGE_SIZE)
  }, [filtered, currentPage])

  useEffect(() => {
    setCurrentPage(1)
  }, [keyword])

  const handleKeywordChange = (value: string) => {
    setKeyword(value)
  }

  const resetSearch = () => {
    setKeyword('')
    setCurrentPage(1)
  }

  const handleView = (row: OpportunityInterestListItem) => {
    navigate(`/allocation-requests/${row.id}`, { state: { listItem: row } })
  }

  return (
    <AllocationRequestsListPresenter
      interests={pageRows}
      isLoading={isLoading}
      keyword={keyword}
      handleKeywordChange={handleKeywordChange}
      resetSearch={resetSearch}
      pagination={pagination}
      currentPage={currentPage}
      handleCurrentPage={setCurrentPage}
      handleView={handleView}
    />
  )
}
